import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPhone, 
  faEnvelope, 
  faCheckCircle, 
  faExclamationTriangle,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp, faFacebook } from '@fortawesome/free-brands-svg-icons';
import emailjs from 'emailjs-com';

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', message: '' });
  const [isSending, setIsSending] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [activeSection, setActiveSection] = useState('form'); // 'form' or 'methods'

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'שם מלא הוא שדה חובה';
    }
    
    if (!formData.phone.trim()) {
      errors.phone = 'מספר טלפון הוא שדה חובה';
    } else if (!/^0\d{8,9}$/.test(formData.phone)) {
      errors.phone = 'יש להזין מספר טלפון תקין';
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'יש להזין כתובת אימייל תקינה';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear field error when user types
    if (fieldErrors[name]) {
      setFieldErrors({
        ...fieldErrors,
        [name]: undefined
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSending(true);
    setFormError(false);

    emailjs
      .send(
        'service_57fj4n9', 
        'template_i1vru3k', 
        formData,
        'wFi9VAP5cqkrjZKFw'
      )
      .then(
        () => {
          setFormSubmitted(true);
          setFormData({ name: '', phone: '', email: '', message: '' });
          setIsSending(false);
        },
        (error) => {
          console.error('Error:', error);
          setFormError(true);
          setIsSending(false);
        }
      );
  };

  const resetForm = () => {
    setFormSubmitted(false);
    setFormError(false);
    setFieldErrors({});
    setFormData({ name: '', phone: '', email: '', message: '' });
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalContainer>
        <CloseButton onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>

        {formSubmitted ? (
          <SuccessContainer>
            <SuccessIcon>
              <FontAwesomeIcon icon={faCheckCircle} />
            </SuccessIcon>
            <SuccessTitle>תודה שיצרתם קשר!</SuccessTitle>
            <SuccessMessage>הפנייה שלך התקבלה בהצלחה. נחזור אליך בהקדם האפשרי.</SuccessMessage>
            <BackButton onClick={resetForm}>חזרה לטופס יצירת קשר</BackButton>
          </SuccessContainer>
        ) : (
          <>
            <ModalHeader>
              <ModalTitle>צור קשר</ModalTitle>
              <TabsContainer>
                <Tab 
                  active={activeSection === 'form'} 
                  onClick={() => setActiveSection('form')}
                >
                  טופס מהיר
                </Tab>
                <Tab 
                  active={activeSection === 'methods'} 
                  onClick={() => setActiveSection('methods')}
                >
                  דרכי התקשרות
                </Tab>
              </TabsContainer>
            </ModalHeader>

            {activeSection === 'methods' && (
              <ContactMethodsContainer>
                <ContactMethod href="tel:+972508857282">
                  <IconWrapper bgColor="#4a6da7">
                    <FontAwesomeIcon icon={faPhone} />
                  </IconWrapper>
                  <MethodDetails>
                    <MethodTitle>התקשרו אלינו</MethodTitle>
                    <MethodDescription>050-885-7282</MethodDescription>
                  </MethodDetails>
                </ContactMethod>
                
                <ContactMethod href="mailto:mashkanta.mishtalemet@gmail.com">
                  <IconWrapper bgColor="#DB4437">
                    <FontAwesomeIcon icon={faEnvelope} />
                  </IconWrapper>
                  <MethodDetails>
                    <MethodTitle>שלחו לנו מייל</MethodTitle>
                    <MethodDescription>mashkanta.mishtalemet@gmail.com</MethodDescription>
                  </MethodDetails>
                </ContactMethod>
                
                <ContactMethod href="https://wa.me/972508857282" target="_blank" rel="noopener noreferrer">
                  <IconWrapper bgColor="#25D366">
                    <FontAwesomeIcon icon={faWhatsapp} />
                  </IconWrapper>
                  <MethodDetails>
                    <MethodTitle>וואטסאפ</MethodTitle>
                    <MethodDescription>שלחו הודעה מיידית</MethodDescription>
                  </MethodDetails>
                </ContactMethod>
                
                <ContactMethod href="https://www.facebook.com/mashkanta.experience" target="_blank" rel="noopener noreferrer">
                  <IconWrapper bgColor="#4267B2">
                    <FontAwesomeIcon icon={faFacebook} />
                  </IconWrapper>
                  <MethodDetails>
                    <MethodTitle>פייסבוק</MethodTitle>
                    <MethodDescription>עקבו אחרינו בפייסבוק</MethodDescription>
                  </MethodDetails>
                </ContactMethod>
                
                <Note>ניתן למלא את הטופס המקוצר להשארת פרטים ואנו נחזור אליכם בהקדם</Note>
              </ContactMethodsContainer>
            )}

            {activeSection === 'form' && (
              <FormContainer>
                <FormHeader>
                  <FormTitle>השאירו פרטים ונחזור אליכם</FormTitle>
                </FormHeader>
                
                {formError && (
                  <ErrorMessage>
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <span>אירעה שגיאה בשליחת הטופס. אנא נסו שוב.</span>
                  </ErrorMessage>
                )}
                
                <ContactForm onSubmit={handleSubmit} noValidate>
                  <FormLabel hasError={!!fieldErrors.name}>
                    <FormLabelText>שם מלא<Required>*</Required></FormLabelText>
                    <FormInput
                      name="name"
                      type="text"
                      placeholder="הכנס את שמך המלא"
                      value={formData.name}
                      onChange={handleChange}
                      hasError={!!fieldErrors.name}
                      required
                    />
                    {fieldErrors.name && <ErrorText>{fieldErrors.name}</ErrorText>}
                  </FormLabel>
                  
                  <FormLabel hasError={!!fieldErrors.phone}>
                    <FormLabelText>טלפון<Required>*</Required></FormLabelText>
                    <FormInput
                      name="phone"
                      type="tel"
                      placeholder="הכנס את מספר הטלפון שלך"
                      value={formData.phone}
                      onChange={handleChange}
                      hasError={!!fieldErrors.phone}
                      required
                    />
                    {fieldErrors.phone && <ErrorText>{fieldErrors.phone}</ErrorText>}
                  </FormLabel>
                  
                  <FormLabel hasError={!!fieldErrors.email}>
                    <FormLabelText>אימייל</FormLabelText>
                    <FormInput
                      name="email"
                      type="email"
                      placeholder="הכנס את כתובת האימייל שלך"
                      value={formData.email}
                      onChange={handleChange}
                      hasError={!!fieldErrors.email}
                    />
                    {fieldErrors.email && <ErrorText>{fieldErrors.email}</ErrorText>}
                  </FormLabel>
                  
                  <SubmitButton type="submit" disabled={isSending}>
                    {isSending ? (
                      <>
                        <LoadingSpinner />
                        <span>שולח...</span>
                      </>
                    ) : (
                      'שלח הודעה'
                    )}
                  </SubmitButton>
                  
                  <BottomNote>
                    לדרכי התקשרות נוספות ניתן לעבור ל
                    <ChangeTabLink onClick={() => setActiveSection('methods')}>
                      דרכי התקשרות
                    </ChangeTabLink>
                  </BottomNote>
                </ContactForm>
              </FormContainer>
            )}
          </>
        )}
      </ModalContainer>
    </Overlay>
  );
};

// Styled Components
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 15px;
  animation: fadeIn 0.2s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContainer = styled.div`
  background: white;
  max-width: 450px;
  width: 100%;
  border-radius: 16px;
  position: relative;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  direction: rtl;
  max-height: 85vh;
  overflow-y: auto;
  animation: slideIn 0.3s ease;
  display: flex;
  flex-direction: column;
  
  @keyframes slideIn {
    from { transform: translateY(30px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }

  @media (max-width: 576px) {
    max-width: 95%;
    max-height: 85vh;
    border-radius: 16px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  left: 15px;
  background: rgba(0, 0, 0, 0.05);
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #555;
  z-index: 10;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
    color: #333;
  }
`;

const ModalHeader = styled.div`
  text-align: center;
  padding: 25px 25px 5px;
  position: relative;
`;

const ModalTitle = styled.h2`
  font-size: 1.8rem;
  color: #192a56;
  margin-bottom: 15px;
  position: relative;
  
  &::after {
    content: "";
    position: absolute;
    bottom: -6px;
    right: calc(50% - 30px);
    width: 60px;
    height: 3px;
    background: linear-gradient(to right, #0076d6, #00a7d6);
    border-radius: 1.5px;
  }
  
  @media (max-width: 576px) {
    font-size: 1.5rem;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin: 20px 0 5px;
`;

const Tab = styled.button`
  background: ${props => props.active ? 'linear-gradient(to right, #f0f8ff, #e7f5ff)' : 'transparent'};
  border: none;
  padding: 8px 15px;
  border-radius: 20px;
  color: ${props => props.active ? '#0076d6' : '#666'};
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  box-shadow: ${props => props.active ? '0 2px 8px rgba(0, 118, 214, 0.15)' : 'none'};

  &:hover {
    background: ${props => props.active ? 'linear-gradient(to right, #f0f8ff, #e7f5ff)' : 'rgba(0, 0, 0, 0.05)'};
  }
`;

const ContactMethodsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 15px 25px 25px;
`;

const ContactMethod = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;
  padding: 12px 16px;
  border-radius: 12px;
  transition: all 0.3s ease;
  background: #f8f9fa;
  
  &:hover {
    background: #f0f4f8;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  }
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => props.bgColor};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const MethodDetails = styled.div`
  margin-right: 15px;
`;

const MethodTitle = styled.div`
  color: #333;
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 3px;
`;

const MethodDescription = styled.div`
  color: #666;
  font-size: 0.85rem;
`;

const Note = styled.div`
  text-align: center;
  margin-top: 15px;
  font-size: 0.9rem;
  color: #666;
  font-style: italic;
`;

const FormContainer = styled.div`
  padding: 5px 25px 25px;
`;

const FormHeader = styled.div`
  text-align: center;
  margin-bottom: 15px;
`;

const FormTitle = styled.h3`
  font-size: 1.2rem;
  color: #333;
  margin-bottom: 5px;
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const FormLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FormLabelText = styled.span`
  font-size: 0.85rem;
  color: #333;
  font-weight: 500;
  display: flex;
  align-items: center;
`;

const Required = styled.span`
  color: #ff6b6b;
  margin-right: 3px;
`;

const FormInput = styled.input`
  padding: 12px;
  border: 1px solid ${props => props.hasError ? '#ff6b6b' : '#dddfe2'};
  border-radius: 8px;
  font-size: 0.9rem;
  color: #333;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ff6b6b' : '#0076d6'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(255, 107, 107, 0.1)' : 'rgba(0, 118, 214, 0.1)'};
  }
  
  &::placeholder {
    color: #a0a0a0;
  }
`;

const ErrorText = styled.div`
  color: #ff6b6b;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  
  &::before {
    content: "⚠";
    margin-left: 5px;
  }
`;

const ErrorMessage = styled.div`
  background-color: #fff5f5;
  border: 1px solid #ffe3e3;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 15px;
  color: #e03131;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.85rem;
  
  svg {
    font-size: 1rem;
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(90deg, #0076d6, #0056a6);
  color: #ffffff;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  box-shadow: 0 4px 15px rgba(0, 118, 214, 0.2);
  position: relative;
  overflow: hidden;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 118, 214, 0.3);
    background: linear-gradient(90deg, #0056a6, #003f87);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    background: #b8c2cc;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const BottomNote = styled.div`
  text-align: center;
  margin-top: 15px;
  font-size: 0.85rem;
  color: #666;
`;

const ChangeTabLink = styled.span`
  color: #0076d6;
  font-weight: 500;
  cursor: pointer;
  margin-right: 5px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 35px 25px;
  animation: fadeIn 0.5s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const SuccessIcon = styled.div`
  font-size: 3.5rem;
  color: #4caf50;
  margin-bottom: 15px;
  
  svg {
    filter: drop-shadow(0 5px 15px rgba(76, 175, 80, 0.3));
  }
`;

const SuccessTitle = styled.h2`
  font-size: 1.6rem;
  color: #192a56;
  margin-bottom: 15px;
`;

const SuccessMessage = styled.p`
  color: #596275;
  font-size: 0.95rem;
  margin-bottom: 25px;
  max-width: 400px;
`;

const BackButton = styled.button`
  background: transparent;
  color: #0076d6;
  border: 2px solid #0076d6;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 118, 214, 0.1);
    transform: translateY(-2px);
  }
`;

export default ContactModal;
